import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import FullScreenConfirmOverlay from '../components/FullScreenConfirmOverlay';
import { showToastError } from '../components/ToastNotifications';
import { useLegacyAppSession } from './LegacyAppSessionContext';
import { startMetrcReauthSignIn } from '../utils/metrcReauthSignIn';
import {
  getErrorMessage,
  getSsoReauthDetails,
  isSsoTemporarilyUnavailable,
  type SsoReauthDetails,
} from '../utils/requestErrors';

type HandleSsoErrorOptions = {
  // Where to come back after signing in. Defaults to the current page.
  returnTo?: string;
};

type SsoReauthContextValue = {
  // Shows the Metrc sign-in prompt for SSO_REAUTH_REQUIRED (409) and SSO_TEMPORARILY_UNAVAILABLE (503).
  // Returns true when the error was handled, so the caller can skip its own error message.
  handleSsoError: (error: unknown, options?: HandleSsoErrorOptions) => boolean;
};

type Prompt =
  | { kind: 'reauth'; details: SsoReauthDetails; returnTo: string }
  | { kind: 'unavailable'; message: string };

const SsoReauthContext = createContext<SsoReauthContextValue | null>(null);

const contactLabel = (contact: SsoReauthDetails['contacts'][number]) =>
  contact.name && contact.mcaEmail ? `${contact.name} (${contact.mcaEmail})` : contact.name || contact.mcaEmail || '';

export function SsoReauthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { org } = useLegacyAppSession();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [busy, setBusy] = useState(false);
  const currentPath = `${location.pathname}${location.search || ''}`;

  const handleSsoError = useCallback((error: unknown, options?: HandleSsoErrorOptions) => {
    if (isSsoTemporarilyUnavailable(error)) {
      setPrompt({ kind: 'unavailable', message: getErrorMessage(error, 'Metrc sign-in is temporarily unavailable.') });
      return true;
    }

    const details = getSsoReauthDetails(error);
    if (!details) return false;
    setPrompt({ kind: 'reauth', details, returnTo: options?.returnTo || currentPath });
    return true;
  }, [currentPath]);

  const close = () => {
    if (!busy) setPrompt(null);
  };

  const signIn = async (details: SsoReauthDetails, returnTo: string) => {
    const organizationId = details.organizationId ?? Number(org?.id);
    if (!organizationId || !details.marketId) {
      showToastError('Metrc sign-in could not be started for this market.');
      setPrompt(null);
      return;
    }

    setBusy(true);
    try {
      await startMetrcReauthSignIn({ organizationId, marketId: details.marketId, returnTo });
    } catch (error) {
      console.error(error);
      showToastError(getErrorMessage(error, 'Metrc sign-in could not be started.'));
      setBusy(false);
      setPrompt(null);
    }
  };

  const value = useMemo(() => ({ handleSsoError }), [handleSsoError]);

  let overlay: ReactNode = null;
  if (prompt?.kind === 'unavailable') {
    overlay = (
      <FullScreenConfirmOverlay
        show
        title="Metrc sign-in unavailable"
        message={`${prompt.message} Your saved Metrc sign-in was kept.`}
        cancelHidden
        confirmLabel="OK"
        onCancel={close}
        onConfirm={close}
        testId="sso-unavailable"
      />
    );
  } else if (prompt?.kind === 'reauth' && prompt.details.canSignIn) {
    const { details, returnTo } = prompt;
    overlay = (
      <FullScreenConfirmOverlay
        show
        title="Metrc sign-in required"
        message="Your Metrc sign-in has expired. Sign in to Metrc again to continue. You will come back to this page."
        cancelLabel="Not now"
        confirmLabel="Sign in to Metrc"
        busy={busy}
        onCancel={close}
        onConfirm={() => signIn(details, returnTo)}
        testId="sso-reauth"
      />
    );
  } else if (prompt?.kind === 'reauth') {
    const contacts = prompt.details.contacts.map(contactLabel).filter(Boolean);
    overlay = (
      <FullScreenConfirmOverlay
        show
        title="Metrc sign-in required"
        message={contacts.length > 0 ? (
          <div data-testid="sso-reauth-contacts">
            <div>The Metrc connection for this facility has expired. Ask one of these people to sign in to Metrc in RetailID:</div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {contacts.map((contact) => <li key={contact}>{contact}</li>)}
            </ul>
          </div>
        ) : (
          'The Metrc connection for this facility has expired. Ask a user in your organization who has a Metrc account to sign in to Metrc in RetailID.'
        )}
        cancelHidden
        confirmLabel="OK"
        onCancel={close}
        onConfirm={close}
        testId="sso-reauth"
      />
    );
  }

  return (
    <SsoReauthContext.Provider value={value}>
      {children}
      {overlay}
    </SsoReauthContext.Provider>
  );
}

const noProvider: SsoReauthContextValue = { handleSsoError: () => false };

// Outside the provider (isolated component tests) nothing is handled, so callers fall back to their own messages.
export function useSsoReauth(): SsoReauthContextValue {
  return useContext(SsoReauthContext) ?? noProvider;
}
